import React, { useEffect, useMemo, useState } from 'react'

import Dialog from '@react/react-spectrum/Dialog'
import { Form, FormItem } from '@react/react-spectrum/Form'
import Radio from '@react/react-spectrum/Radio'
import RadioGroup from '@react/react-spectrum/RadioGroup'
import Select from '@react/react-spectrum/Select'
import Textfield from '@react/react-spectrum/Textfield'
import Wait from '@react/react-spectrum/Wait'

import MediaUpload from 'components/MediaUpload'
import DealRegistrationInput from 'components/DealRegistrationInput'

import { PROJECT_TYPES } from '../../../config/constants'
import A from '../../../services/analytics'
import { getProjectTemplates } from 'services/project'

import './NewProjectDialog.scss'

// FUTURE: Unhide the Mobile V2 project once it is out of Beta
const projectTypes = Object.values(PROJECT_TYPES).filter((type) => type.value !== 'mobile-v2')

const NewProjectDialog = ({ onProjectCreate, onDrNumberSubmit, ...otherProps }) => {
  const [projectTemplates, setProjectTemplates] = useState([])
  const [state, setState] = useState({
    type: projectTypes[0].value,
    template: null,
    title: '',
    thumbnail: '',
  })
  const [validation, setValidation] = useState({ title: 'invalid', template: 'invalid', ok: false })

  /* 
    Use projectTypes.filter() to feature-flag a project type 
    "web-modular" is filtered out for it to be included together with "web" templates
  */
  const projectTypeOptions = projectTypes
    .filter((opt) => !opt.hidden)
    .filter((opt) => !['web-modular', 'customer-experience-v2'].includes(opt.value))
    .map((opt) => ({
      ...opt,
      disabled: !getTemplates(projectTemplates, opt.value)?.length,
    }))

  /*
    Template options to show in drop-down after selecting a type are filtered by matching template.type with project.type
    Two exceptions are web-modular projects merged with web option and cx app JT template as journey-trigger type
  */
  const templateOptions = useMemo(
    () =>
      getTemplates(projectTemplates, state.type)
        .map((t) => ({ ...t, label: t.title || t.id, value: t.id }))
        .sort((a, b) => a.order - b.order),
    [projectTemplates, state.type]
  )

  const handleChange = (fieldName) => (value) => {
    if (fieldName === 'type') {
      const templateConfig = projectTemplates.find((t) => t.type === value)
      setState({
        ...state,
        type: value,
        template: templateConfig?.id,
        thumbnail: templateConfig?.image,
      })
    } else if (fieldName === 'template') {
      const templateConfig = projectTemplates.find((t) => t.id === value)
      setState({
        ...state,
        template: value,
        thumbnail: templateConfig?.image,
      })
    } else {
      setState({
        ...state,
        [fieldName]: value,
      })
    }
  }

  const validate = () => {
    const isValidTitle = state.title
    const isValidTemplate = state.template

    setValidation({
      title: isValidTitle ? 'valid' : 'invalid',
      template: isValidTemplate ? 'valid' : 'invalid',
      ok: isValidTitle && isValidTemplate,
    })
  }

  const onSubmit = async () => {
    const project = await onProjectCreate(state.template, state.title, state.thumbnail)
    if (state.drNumber && project.id) {
      onDrNumberSubmit(state.drNumber, project.id)
    }
  }

  useEffect(() => {
    A.triggerCreateNewProject()
    getProjectTemplates().then(setProjectTemplates)
  }, [])

  useEffect(() => {
    setState((p) => {
      const { id, image } = templateOptions[0] || {}
      return {
        ...p,
        template: id,
        thumbnail: image,
      }
    })
  }, [templateOptions])

  useEffect(validate, [state])

  return (
    <Dialog
      title='New project'
      confirmLabel='Create'
      cancelLabel='Cancel'
      onConfirm={onSubmit}
      confirmDisabled={!validation.ok}
      className='new-project-dialog'
      {...otherProps}
      role='dialog'
    >
      <Form aria-labelledby='create-project'>
        <section>
          <FormItem label='Title'>
            <Textfield
              name='title'
              value={state.title || ''}
              onChange={handleChange('title')}
              validationState={validation.title}
            />
          </FormItem>
          <FormItem label='Thumbnail'>
            <MediaUpload
              type='image'
              value={state.thumbnail}
              onUpload={handleChange('thumbnail')}
              withTextfield={true}
              projectId='new-project'
            />
          </FormItem>

          <FormItem label='DR number'>
            <DealRegistrationInput defaultValue='' onChange={handleChange('drNumber')} />
          </FormItem>
        </section>

        <section className={'projectTemplate'}>
          {!projectTemplates && <Wait size={'L'} />}
          <FormItem label='Type'>
            <RadioGroup name='type' vertical onChange={handleChange('type')}>
              {projectTypeOptions.map((type, idx) => (
                <Radio
                  label={type.label}
                  value={type.value}
                  key={type.value}
                  checked={idx === 0}
                  disabled={type.disabled}
                />
              ))}
            </RadioGroup>
          </FormItem>
          <FormItem label='Template'>
            <Select
              aria-label='Template'
              options={templateOptions}
              value={state.template}
              onChange={handleChange('template')}
            />
          </FormItem>

          <FormItem label=''>
            <div className='description'>
              <em>{templateOptions.find((t) => t.value === state.template)?.description}</em>
            </div>
          </FormItem>
        </section>
      </Form>
    </Dialog>
  )
}

export default NewProjectDialog

function getTemplates(allTemplates, type) {
  return allTemplates.filter((t) => {
    switch (type) {
      case 'web':
        return t.type === 'web' || t.type === 'web-modular'
      case 'journey-trigger':
        return t.type === 'journey-trigger' || t.id.includes('journey-trigger')
      case 'customer-experience':
        return ['customer-experience', 'customer-experience-v2'].includes(t.type) && !t.id.includes('journey-trigger')
      default:
        return t.type === type
    }
  })
}
